import { Component, computed, inject, input, OnInit, signal, PLATFORM_ID, } from '@angular/core';
import { BidderCommunityMenuService } from '../../../services/bidder-community/bidder-community-menu/bidder-community-menu.service';
import { FavoritesPageComponent } from './favorites-page/favorites-page.component';
import { PaginatorModule } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FavoritesItemsService } from '../../../services/favorites/favorites-items.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { AuthService } from '../../../services/authentication/auth.service';
import { environment } from '../../../../environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-favorites',
  imports: [FavoritesPageComponent,
    PaginatorModule,
    SelectModule,
    CommonModule,
    FormsModule,
    ProgressSpinnerModule
  ],
  templateUrl: './favorites.component.html',
  styleUrl: './favorites.component.scss'
})
export class FavoritesComponent implements OnInit {

  public id = input(0);
  private readonly bidderCommunityMenuService = inject(BidderCommunityMenuService);
  private readonly favoritesService = inject(FavoritesItemsService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);

  authSubscription!: Subscription;

  isAuthenticated = this.oidcSecurityService.authenticated;
  items = this.favoritesService.itemFavoriteSignal;
  isLoading = this.favoritesService.isLoading;

  // paginator
  page: number = 0;
  rowsPerPageOptions = [1, 2, 6, 10];
  first = signal(0);
  rows = signal(6);

  selectedSortSignal = signal('TIMELEFT_ASC');

  paginatedItems = computed(() =>
    this.sortedItems().slice(this.first(), this.first() + this.rows())
  );

  sortFilters = [
    { label: 'Item Name - Descending', value: 'NAME_DESC' },
    { label: 'Item Name - Ascending', value: 'NAME_ASC' },
    { label: 'Org Name - Descending', value: 'ORG_DESC' },
    { label: 'Org Name - Ascending', value: 'ORG_ASC' },
    { label: 'Max Bid - Ascending', value: 'MAXBID_ASC' },
    { label: 'Max Bid - Descending', value: 'MAXBID_DESC' },
    { label: 'Leading Bid - Ascending', value: 'HIGHBID_ASC' },
    { label: 'Leading Bid - Descending', value: 'HIGHBID_DESC' },
    { label: 'Time Left - Ascending', value: 'TIMELEFT_ASC' },
    { label: 'Time Left - Descending', value: 'TIMELEFT_DESC' },
  ];

  sortedItems = computed(() => {
    const sortValue = this.selectedSortSignal();
    return [...this.items()].sort((a, b) => {

      switch (sortValue) {
        case 'NAME_ASC':
          return a.itemName.localeCompare(b.itemName);
        case 'NAME_DESC':
          return b.itemName.localeCompare(a.itemName);

        case 'ORG_ASC':
          return a.orgName.localeCompare(b.orgName);
        case 'ORG_DESC':
          return b.orgName.localeCompare(a.orgName);

        case 'MAXBID_ASC': {
          const aMax = parseFloat((a.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          const bMax = parseFloat((b.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          return aMax - bMax;
        }

        case 'MAXBID_DESC': {
          const aMax = parseFloat((a.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          const bMax = parseFloat((b.myMaxBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          return bMax - aMax;
        }

        case 'HIGHBID_ASC': {
          const aHigh = parseFloat((a.leadingBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          const bHigh = parseFloat((b.leadingBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          return aHigh - bHigh;
        }

        case 'HIGHBID_DESC': {
          const aHigh = parseFloat((a.leadingBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          const bHigh = parseFloat((b.leadingBid ?? '0').toString().replace(/[^0-9.]/g, '')) || 0;
          return bHigh - aHigh;
        }
        case 'TIMELEFT_DESC':
          {
            const now = new Date();
            const aClosed = new Date(a.closingDate) < now;
            const bClosed = new Date(b.closingDate) < now;

            if (aClosed !== bClosed) {
              return aClosed ? -1 : 1;
            }

            const diff = new Date(b.closingDate).getTime() - new Date(a.closingDate).getTime();
            if (diff !== 0) return diff;

            return b.itemName.localeCompare(a.itemName);
          }
        case 'TIMELEFT_ASC':
        default: {
          const now = new Date();
          const aClosed = new Date(a.closingDate) < now;
          const bClosed = new Date(b.closingDate) < now;

          if (aClosed !== bClosed) {
            return aClosed ? 1 : -1;
          }

          const diff = new Date(a.closingDate).getTime() - new Date(b.closingDate).getTime();
          if (diff !== 0) return diff;

          return a.itemName.localeCompare(b.itemName);
        }
      }
    });
  });

  ngOnInit(): void {
    setTimeout(() => {
      this.bidderCommunityMenuService.setPageData(
        {
          id: this.id(),
          pageName: "Favorites",
          pageRoute: "favorites"
        }
      )
      if (typeof window !== 'undefined') {
        window.onpageshow = function (event) {
          if (event.persisted) {
            window.location.reload();
          }
        };
      }

      if (this.isAuthenticated().isAuthenticated) {
        this.getFavorites();
      } else {
        if (isPlatformBrowser(this.platformId)) {
          if (typeof window !== 'undefined') {
            window.open(environment.mediaUrl, '_self');
          }
          return;
        }
      }

      this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
        if (isAuth) {
          this.getFavorites();
        } else {
          if (isPlatformBrowser(this.platformId)) {
            window.location.replace(environment.webUrl);
            return;
          }
        }
      });
    }, 100);
  }
  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
  }

  getFavorites() {
    this.favoritesService.getFavorites().subscribe();
  }

  onRemoveFavorite(itemId: number) {
    const newItems = this.items().filter(i => i.itemId !== itemId);
    this.items.set(newItems);

    const totalNewItems = newItems.length;
    if (this.first() >= totalNewItems && totalNewItems > 0) {
      this.first.set(Math.max(0, totalNewItems - this.rows()));
    }
  }

  onPageChange(event: any) {
    this.first.set(event.first);
    this.rows.set(event.rows);
  }


}
